import { Slot, Slottable } from "@radix-ui/react-slot";
import { cn } from "@theprelude/core";
import { cva, VariantProps } from "class-variance-authority";
import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";
import { Loading } from "../icon-helpers/loading";

const buttonVariants = cva(
  "relative flex w-max cursor-pointer flex-row items-center gap-2 rounded-[4px] text-left font-medium disabled:cursor-not-allowed disabled:border-secondary-foreground disabled:bg-surface-background disabled:text-secondary-foreground",
  {
    variants: {
      intent: {
        primary:
          "border-blue-bay bg-blue-bay text-white hover:border-primary-foreground hover:bg-blueberry",
        "primary-pill":
          "max-h-6 rounded-xl border border-blue-bay bg-blue-bay font-medium text-white hover:border-primary-foreground hover:bg-blueberry",
        secondary:
          "border border-sheet-metal bg-surface-background text-primary-foreground hover:border-primary-foreground",
        positive:
          "border-status-green bg-status-green text-primary-background hover:bg-transparent-black hover:text-status-green",
        negative:
          "border border-status-red bg-status-red text-surface-background hover:border-primary-foreground",
        "negative-inverted":
          "border border-status-red bg-surface-background text-status-red hover:bg-status-red hover:text-primary-background",
        caution:
          "border border-status-yellow bg-status-yellow text-carbon hover:border-primary-foreground",
        pill: "max-h-[1.625rem] rounded-xl border border-stroke-line bg-surface-background font-normal hover:border-stroke-input",
        "text-link":
          "font-normal text-primary-foreground text-xs underline underline-offset-2",
      },
      size: {
        none: "",
        tiny: "px-2 py-1 text-xs leading-5",
        small: "px-3 py-2 text-sm leading-5",
        regular: "px-3 py-2 text-base leading-5",
        medium: "px-4 py-3 text-base leading-6",
        large: "px-7 py-4 text-lg leading-6",
      },
    },
    compoundVariants: [
      {
        intent: "pill",
        size: "small",
        class: "px-2 py-1 leading-4",
      },
      {
        intent: [
          "pill",
          "primary",
          "primary-pill",
          "secondary",
          "positive",
          "negative",
          "negative-inverted",
          "caution",
        ],
        class: "hover:drop-shadow-[2px_2px_0px_rgba(0,0,0,0.25)]",
      },
    ],
    defaultVariants: {
      size: "small",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  loading?: boolean;
  extraIcon?: ReactNode;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      intent,
      size,
      iconPosition = "left",
      icon,
      extraIcon,
      loading,
      children,
      asChild,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const Icon = loading ? (
      <Loading className="mx-0" data-testid="button-loading" />
    ) : (
      icon
    );
    return (
      <Comp
        ref={ref}
        {...props}
        className={cn(
          buttonVariants({
            className,
            intent,
            size,
          }),
        )}
      >
        {iconPosition === "left" && Icon}
        {children && <Slottable>{children}</Slottable>}
        {iconPosition === "right" && Icon}
        {extraIcon}
      </Comp>
    );
  },
);

Button.displayName = "Button";
